import { defineNuxtRouteMiddleware, abortNavigation, useError } from "nuxt/app";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useBasketStore } from "~/stores/basket";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import { useBugReporter, usePinia } from "#imports";

const abortWithUnexpectedError = (
  message: string = "Unexpected error trying to get the basket",
) => {
  return abortNavigation({
    message,
    statusCode: 500,
  });
};

export default defineNuxtRouteMiddleware(async (to) => {
  if (useError().value) return;

  const routeOrderingMode = Array.isArray(to.params.orderingMode)
    ? to.params.orderingMode[0]
    : to.params.orderingMode;

  if (!routeOrderingMode) {
    return abortWithUnexpectedError(
      "EnsureBasketIsLoadedMiddleware requires a route with orderingMode parameter.",
    );
  }

  const pinia = usePinia();
  const outlet = useOutletStore(pinia);

  if (!outlet.isLoaded) {
    return abortWithUnexpectedError(
      "EnsureBasketIsLoadedMiddleware outlet must be loaded before.",
    );
  }

  const basket = useBasketStore(pinia);

  if (
    basket.isLoaded &&
    basket.outletId === outlet.id &&
    basket.orderingMode === routeOrderingMode
  ) {
    return;
  }

  const business = useBusinessStore(pinia);
  const { report } = useBugReporter();

  try {
    await basket.loadBasket(
      business.id,
      business.currency,
      outlet.id,
      // TODO: fix mode to match domain
      routeOrderingMode as IDomainOrderingMode,
      business.salesChannel,
    );
  } catch (error) {
    report(error, {
      tags: {
        "code.flag": "middleware-basket",
      },
    });

    return abortWithUnexpectedError();
  }
});
